import _extends from "@babel/runtime/helpers/esm/extends";
import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import React, { Children, forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { addMonths, addYears, format, getMonth, getYear, isAfter, isBefore, isSameDay, isSameMonth, isWithinInterval } from 'date-fns';
import Box from '../Box';
import Button from '../Button';
import { useThemeValue } from '../system';
import Group from '../Group';
import HeadingUppercase from '../HeadingUppercase';
import StackView from '../StackView';
import { useHover } from '../hooks';
import Day from './Day';
import { getMonthWeekDays, getMonthWeeks, getNextOccurrenceOfDay, getYears, MAX_MONTH_DEFAULT, MAX_YEAR_DEFAULT, MIN_MONTH_DEFAULT, MIN_YEAR_DEFAULT, MONTHS, TODAY } from './utils';
var sizes = {
  sm: {
    buttonSize: 'sm',
    cellSize: 4,
    fontSize: 4,
    trimWeekdays: 2,
    headerFontSize: 4
  },
  md: {
    buttonSize: 'sm',
    cellSize: 5,
    fontSize: 4,
    trimWeekdays: 3,
    headerFontSize: 2
  },
  lg: {
    buttonSize: 'sm',
    cellSize: 7,
    fontSize: 3,
    trimWeekdays: 3,
    headerFontSize: 2
  }
};
var Calendar = /*#__PURE__*/forwardRef(function (_ref, calendarRef) {
  var date = _ref.date,
      initialDate = _ref.initialDate,
      maxDate = _ref.maxDate,
      minDate = _ref.minDate,
      onDateChange = _ref.onDateChange,
      onDateSelect = _ref.onDateSelect,
      renderDay = _ref.renderDay,
      selected = _ref.selected,
      size = _ref.size,
      weekStartsOn = _ref.weekStartsOn,
      restProps = _objectWithoutPropertiesLoose(_ref, ["date", "initialDate", "maxDate", "minDate", "onDateChange", "onDateSelect", "renderDay", "selected", "size", "weekStartsOn"]);

  var calendarTheme = useThemeValue('calendar');
  var lastDate = useRef(date);

  var _useHover = useHover(),
      hover = _useHover.hover,
      hoverProps = _useHover.hoverProps;

  var _useState = useState(date || initialDate),
      currentDate = _useState[0],
      setCurrentDate = _useState[1];

  var _useState2 = useState('calendar'),
      currentView = _useState2[0],
      setCurrentView = _useState2[1];

  var isCalendarView = currentView === 'calendar';
  var isYearsView = currentView === 'years';
  var isMonthsView = currentView === 'months';
  var currentMonth = getMonth(currentDate);
  var currentYear = getYear(currentDate);
  var currentYears = getYears(currentYear);
  var minMonth = getMonth(minDate) || MIN_MONTH_DEFAULT;
  var maxMonth = getMonth(maxDate) || MAX_MONTH_DEFAULT;
  var minYear = getYear(minDate) || MIN_YEAR_DEFAULT;
  var maxYear = getYear(maxDate) || MAX_YEAR_DEFAULT;
  var _sizes$size = sizes[size],
      buttonSize = _sizes$size.buttonSize,
      cellSize = _sizes$size.cellSize,
      fontSize = _sizes$size.fontSize,
      headerFontSize = _sizes$size.headerFontSize,
      trimWeekdays = _sizes$size.trimWeekdays;
  var weekdays = getMonthWeekDays({
    trimWeekdays: trimWeekdays,
    weekStartsOn: weekStartsOn || calendarTheme.weekStartsOn || 0
  });
  var weeks = getMonthWeeks(currentDate, {
    forceSixWeeks: true,
    weekStartsOn: weekStartsOn || calendarTheme.weekStartsOn || 0
  });

  var setDate = function setDate(nextDate, moveToCalendarView) {
    var nextView = moveToCalendarView ? 'calendar' : currentView;

    if (Calendar.isBeforeDay(nextDate, minDate)) {
      nextDate = minDate;
    } else if (Calendar.isAfterDay(nextDate, maxDate)) {
      nextDate = maxDate;
    }

    if (date === null) {
      setCurrentDate(nextDate);
      setCurrentView(nextView);
    } else {
      setCurrentView(nextView);
    }

    if (onDateChange) {
      onDateChange(nextDate);
    }
  };

  var navigateCalendar = function navigateCalendar(direction) {
    var isYearsView = currentView === 'years';
    var add = isYearsView ? addYears : addMonths;
    setDate(add(currentDate, direction));
  };

  useEffect(function () {
    if (date !== null && !isSameDay(lastDate.current, date)) {
      setCurrentDate(date);
    }

    lastDate.current = date;
  }, [date]);
  useImperativeHandle(calendarRef, function () {
    return {
      setDate: setDate,
      navigateCalendar: navigateCalendar
    };
  });
  return /*#__PURE__*/React.createElement(StackView, _extends({
    innerRef: calendarRef
  }, hoverProps, restProps), /*#__PURE__*/React.createElement(StackView, {
    axis: "horizontal",
    alignment: "center",
    distribution: "space-between",
    padding: 0.5,
    paddingBottom: 0
  }, /*#__PURE__*/React.createElement(StackView, {
    axis: "horizontal",
    spacing: 0.5
  }, /*#__PURE__*/React.createElement(Box, {
    as: "button",
    "aria-label": isMonthsView ? 'Choose dates' : 'Choose month',
    padding: 0.25,
    fontSize: headerFontSize,
    lineHeight: 3,
    radius: 3,
    cursor: "pointer",
    backgroundColor: hover ? 'highlight' : 'transparent',
    hover: {
      backgroundColor: 'highlightSecondary'
    },
    onClick: function onClick() {
      setCurrentView(isMonthsView ? 'calendar' : 'months');
    }
  }, format(currentDate, 'MMMM')), /*#__PURE__*/React.createElement(Box, {
    as: "button",
    "aria-label": isYearsView ? 'Choose dates' : 'Choose year',
    padding: 0.25,
    fontSize: headerFontSize,
    lineHeight: 3,
    radius: 3,
    cursor: "pointer",
    backgroundColor: hover ? 'highlight' : 'transparent',
    hover: {
      backgroundColor: 'highlightSecondary'
    },
    onClick: function onClick() {
      setCurrentView(isYearsView ? 'calendar' : 'years');
    }
  }, currentYear)), /*#__PURE__*/React.createElement(Group, {
    marginRight: 0.5,
    childProps: {
      variant: 'outline',
      size: buttonSize
    }
  }, /*#__PURE__*/React.createElement(Button, {
    title: "Previous month",
    icon: {
      name: 'caret-left'
    },
    disabled: isYearsView ? currentYear <= minYear : isSameMonth(currentDate, minDate),
    onClick: function onClick() {
      return navigateCalendar(-1);
    }
  }), /*#__PURE__*/React.createElement(Button, {
    tooltip: {
      title: 'Today'
    },
    icon: {
      name: 'radio-1'
    },
    onClick: function onClick() {
      return setDate(TODAY, true);
    }
  }), /*#__PURE__*/React.createElement(Button, {
    title: "Next month",
    icon: {
      name: 'caret-right'
    },
    disabled: isYearsView ? currentYear >= maxYear : isSameMonth(currentDate, maxDate),
    onClick: function onClick() {
      return navigateCalendar(1);
    }
  }))), isMonthsView && /*#__PURE__*/React.createElement(StackView, {
    axis: "horizontal",
    width: cellSize * 7,
    height: cellSize * 6 + 3.5,
    marginTop: 0.5,
    wrap: true
  }, MONTHS.map(function (month, monthIndex) {
    var monthDisabled = !Calendar.isWithinRange(new Date(currentYear, monthIndex, 1), {
      start: new Date(minYear, minMonth, 1),
      end: new Date(maxYear, maxMonth, 1)
    });
    return /*#__PURE__*/React.createElement(StackView, {
      key: month,
      as: "button",
      basis: cellSize * 7 / 3,
      alignment: "center",
      distribution: "center",
      fontWeight: 700,
      cursor: monthDisabled ? 'not-allowed' : 'pointer',
      backgroundColor: currentMonth === monthIndex ? 'surfaceSecondary' : 'transparent',
      opacity: monthDisabled ? 0.6 : undefined,
      hover: {
        backgroundColor: monthDisabled ? undefined : 'highlight'
      },
      disabled: monthDisabled,
      onClick: function onClick() {
        var monthDifference = monthIndex - currentMonth;
        setDate(addMonths(currentDate, monthDifference), true);
      },
      children: /*#__PURE__*/React.createElement(HeadingUppercase, {
        cursor: "inherit"
      }, month.substring(0, 3))
    });
  })), isYearsView && /*#__PURE__*/React.createElement(StackView, {
    axis: "horizontal",
    width: cellSize * 7,
    height: cellSize * 6 + 3.5,
    marginTop: 0.5,
    wrap: true
  }, currentYears.map(function (year) {
    var yearDisabled = !(year >= minYear && year <= maxYear);
    return /*#__PURE__*/React.createElement(StackView, {
      key: year,
      as: "button",
      basis: cellSize * 7 / 3,
      alignment: "center",
      distribution: "center",
      fontWeight: 700,
      cursor: yearDisabled ? 'not-allowed' : 'pointer',
      backgroundColor: currentYear === year ? 'surfaceTertiary' : 'transparent',
      opacity: yearDisabled ? 0.6 : undefined,
      hover: {
        backgroundColor: yearDisabled ? undefined : 'highlight'
      },
      disabled: yearDisabled,
      onClick: function onClick() {
        var yearDifference = year - currentYear;
        setDate(addYears(currentDate, yearDifference), true);
      },
      children: /*#__PURE__*/React.createElement(HeadingUppercase, {
        cursor: "inherit"
      }, year)
    });
  })), isCalendarView && /*#__PURE__*/React.createElement(StackView, null, /*#__PURE__*/React.createElement(StackView, {
    axis: "horizontal"
  }, weekdays.map(function (weekday) {
    return /*#__PURE__*/React.createElement(StackView, {
      key: weekday.full,
      alignment: "center",
      distribution: "center",
      width: cellSize,
      height: 4
    }, /*#__PURE__*/React.createElement(HeadingUppercase, {
      fontSize: 6
    }, weekday.trimmed));
  })), weeks.map(function (week, index) {
    return /*#__PURE__*/React.createElement(StackView, {
      key: index,
      axis: "horizontal"
    }, Children.toArray(week.map(function (date) {
      var inSameMonth = isSameMonth(date, currentDate);
      var isMinDate = isBefore(date, minDate);
      var isMaxDate = isAfter(date, maxDate);
      var isSelected = isSameDay(date, selected);
      var isToday = isSameDay(date, TODAY);
      var backgroundColor = isMinDate || isMaxDate ? 'surfaceSecondary' : 'inherit';
      var color = isSelected ? 'white' : inSameMonth ? 'inherit' : 'foregroundTertiary';
      return renderDay({
        backgroundColor: backgroundColor,
        cellSize: cellSize,
        color: color,
        date: date,
        fontSize: fontSize,
        inSameMonth: inSameMonth,
        isMinDate: isMinDate,
        isMaxDate: isMaxDate,
        isSelected: isSelected,
        isToday: isToday,
        selectDate: onDateSelect
      });
    })));
  })));
});
Calendar.defaultProps = {
  date: null,
  initialDate: TODAY,
  minDate: null,
  maxDate: null,
  renderDay: function renderDay(props) {
    return /*#__PURE__*/React.createElement(Day, props);
  },
  size: 'md'
};
Calendar.TODAY = TODAY;
Calendar.Day = Day;
Calendar.getNextOccurrenceOfDay = getNextOccurrenceOfDay;

Calendar.isAfterDay = function (date, dateToCompare) {
  return date && dateToCompare && isAfter(date, dateToCompare);
};

Calendar.isBeforeDay = function (date, dateToCompare) {
  return date && dateToCompare && isBefore(date, dateToCompare);
};

Calendar.isSameDay = function (dateLeft, dateRight) {
  return dateLeft && dateRight && isSameDay(dateLeft, dateRight);
};

Calendar.isWithinRange = function (date, _ref2) {
  var start = _ref2.start,
      end = _ref2.end;

  if (date && start && end && isAfter(end, start)) {
    return isWithinInterval(date, {
      start: start,
      end: end
    });
  }
};

export default Calendar;